import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Mail, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ahsga-portal/artifacts/h8agb7mt_ahsga_circle_icon_enhanced.png%201.png';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(email)}`);
      setSubmitted(true);
      toast.success('If an account exists with this email, a reset link has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-md">
          <Card className="bg-white/95 backdrop-blur-md shadow-2xl border border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <img src={LOGO_URL} alt="AHSGA Logo" className="w-16 h-16 object-contain mx-auto" />
                <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto flex items-center justify-center">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-serif font-bold text-slate-900">Check Your Email</h2>
                <p className="text-slate-600">
                  If an account exists for <strong>{email}</strong>, we've sent password reset instructions.
                </p>
                <p className="text-sm text-slate-500">
                  The link will expire in 1 hour. Don't forget to check your spam folder.
                </p>
                <Link to="/member/login">
                  <Button className="w-full mt-4" data-testid="back-to-login-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        <Card className="bg-white/95 backdrop-blur-md shadow-2xl border border-slate-200">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src={LOGO_URL} alt="AHSGA Logo" className="w-16 h-16 object-contain" />
            </div>
            <CardTitle className="text-3xl font-serif font-bold text-slate-900">
              Forgot Password?
            </CardTitle>
            <p className="text-slate-600 mt-2">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="you@ahsga.me"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/member/login"
                data-testid="back-to-login-link"
                className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-white mt-6 opacity-80">
          © 2026 AHSGA. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
